// The Compensation queue's and worksheet's words (lane B2 S09): the figures of his Compensation task, in the order he
// gives them — "Compensation receives automatic parallel task for grade, market data, recommendation, recommended
// range, and commission structure." — with the restricted rows of BRD chapter 6 (G §13). Every entry quotes his
// glossary; the row names themselves live in views/requisition/labels.ts (COMP_ROW_LABELS).

import { defineLabels, template } from "@/mc/labels";

const TASK = "\"Compensation receives automatic parallel task for grade, market data, recommendation, recommended range, and commission structure.\"";
const ROW5 = "| Market data / internal range / positioning | Row 5: HM No · others Full · Interviewer No.";
const ROW6 = "| Internal equity / working notes / approval evidence | Row 6: HM \"No or own-action only\" · others Full · Interviewer No.";
// Lines assembled from his words that wait for his own wording (DS §6 Q-E15, UAT F-27, F-54, F-55).
const QE15_RANGE = "*Min* · *Max* · *Currency* (the Compensation worksheet's range fields)";

/** The worksheet's fields. */
export const COMP_FIELD_LABELS = /* @__PURE__ */ defineLabels({
  marketData: { text: "Market data" },
  recommendation: { text: "Recommendation" },
  recommendedRange: { text: "Recommended range" },
  commissionStructure: { text: "Commission structure" },
  internalRange: { text: "Internal range" },
  positioning: { text: "Positioning" },
  workingNotes: { text: "Working notes" },
  approvalEvidence: { text: "Approval evidence" },
  min: { text: "Min" },
  max: { text: "Max" },
  currency: { text: "Currency" },
});

// B13 (DC-230, F-154 / F-155): the one card *Hiring range*, its five section headers, its fields and its error words.
const HIRING_RANGE = "| Hiring range | The Compensation worksheet's one card (DC-230), named from his visibility row \"Recommendation / recommended hiring range\". Its sections follow his task list above, each a section header over its fields: **Grade** · **Market data** · **Recommendation** · **Bonus / commission** · **Working notes / approval evidence**.";
const MARKET_MID = "| Market mid-point | The one **Market data** field until PayFactors is live";
const RECOMMENDED = "| Recommended hiring range | His row 1 word, drawn as ONE labelled row: a Min and a Max (DS Q-E15) in the grade's currency, which the grade fixes (no currency box), with the band check beside it.";
const POSITIONING = "| Positioning · Below mid / At mid / Above mid · Positioning note | **Positioning** is a choice **Below mid · At mid · Above mid** (against the grade's mid), required to finalize, with an optional **Positioning note** of up to 80 characters.";
const BONUS = "| Bonus target % · Commission | **Bonus target %** (0–100) and **Commission**, a toggle: off reads None; on opens **Commission structure** (his word).";
const NOT_SAVED = "A required field left empty reads **Required**; a filled field the server has not seen reads **Not saved yet**.";
const COUNTER = "| Near a length limit | a counter under the field from 80 % of the limit | *{n} / {max}* |";
// The worksheet's words assembled to complete his 12 Oct ruling, waiting for his own wording (DS §6 Q-E15).
const QE15_WORKSHEET =
  "the Hiring range worksheet (DC-230, assembled to complete Tejesh's 12 Oct ruling): the band check *Within the internal range* · *Below the internal range* · *Above the internal range*; the field errors *Max is below Min* · *At most {max} characters* · *Between {min} and {max}*; the read-only line *{min} – {max} {currency} · mid {mid}*; Finalize's description *Finalize freezes this version: the grade, the range, the positioning, bonus, commission, notes and evidence. The range goes to the requisition.*";

/** The Hiring range card (DC-230): its title, its five section headers and the fields it adds. */
export const HIRING_RANGE_LABELS = /* @__PURE__ */ defineLabels({
  hiringRange: { text: "Hiring range" },
  grade: { text: "Grade" },
  marketData: { text: "Market data" },
  recommendation: { text: "Recommendation" },
  bonusCommission: { text: "Bonus / commission" },
  notesEvidence: { text: "Working notes / approval evidence" },
  marketMid: { text: "Market mid-point" },
  recommendedHiringRange: { text: "Recommended hiring range" },
  belowMid: { text: "Below mid" },
  atMid: { text: "At mid" },
  aboveMid: { text: "Above mid" },
  positioningNote: { text: "Positioning note" },
  bonusTargetPct: { text: "Bonus target %" },
  commission: { text: "Commission" },
  none: { text: "None" },
  notSavedYet: { text: "Not saved yet" },
  withinInternal: { text: "Within the internal range" },
  belowInternal: { text: "Below the internal range" },
  aboveInternal: { text: "Above the internal range" },
  maxBelowMin: { text: "Max is below Min" },
  finalizeFreezes: { text: "Finalize freezes this version: the grade, the range, the positioning, bonus, commission, notes and evidence. The range goes to the requisition." 


},
});

/** A text over its server limit (the text is kept, never cut). */
export const atMostCharacters = /* @__PURE__ */ template({ text: "At most {max} characters" });
/** A number outside its bounds. */
export const betweenBounds = /* @__PURE__ */ template({ text: "Between {min} and {max}" });
/** The grade's internal range, read-only. */
export const internalRangeLine = /* @__PURE__ */ template({ text: "{min} – {max} {currency} · mid {mid}" });
/** The counter under a field from 80 % of its limit. */
export const lengthCounter = /* @__PURE__ */ template({ text: "{n} / {max}" });

/** Compensation's start of the review (Pending → In Review), named for what it does. */
export const COMP_ACTION_LABELS = /* @__PURE__ */ defineLabels({
  startReview: { text: "Start review" },
});

/** An empty Compensation queue tab: no review has its status. */
export const noCompensationReview = /* @__PURE__ */ template({ text: "No Compensation review is {status}" 

});

/** His sentences on the worksheet. */
export const COMP_RULES = /* @__PURE__ */ defineLabels({
  task: { text: "Compensation receives automatic parallel task for grade, market data, recommendation, recommended range, and commission structure." 


},
  restricted: { text: "Restricted fields must also be absent from search, exports, reports, email, attachments, audit summaries and APIs." 


},
  startToEnter: { text: "Start the review to enter grade and range" 


},
});
